import { Stack } from 'component-library';
import { FC, HTMLAttributes, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { BenchmarkType, PlotReportFactElement, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { Logger } from '@/lib/logs/logger';

import { Bar, BarChart, Chart, TooltipProps } from './components/Chart';
import { CustomActiveBar } from './components/CustomActiveBar';
import { RenderBenchmarkReferenceLine } from './components/ReferenceLine';
import { ChartTooltip, ChartTooltipTextPrimary, ChartTooltipTextSecondary } from './components/Tooltip';
import { NoChartData } from './NoChartData';
import { DefaultChartProps } from './types';
import { getBenchmarkLabel } from './utils';

const BAR_COLOR = '#456b5e';
const RECTANGLE_RADIUS = 4;

type ChartDataEntry = { category: string; count: number };

const getChartData = (data: PlotReportFactElement[]): ChartDataEntry[] =>
  data
    .flatMap((fact) => {
      const category = fact.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.class_name)?.value;
      const count = typeof fact.value === 'number' ? fact.value : null;
      return category && count ? [{ category: String(category), count }] : [];
    })
    .sort((a, b) => b.count - a.count);

export const InvasiveSpeciesChart: FC<
  HTMLAttributes<HTMLDivElement> &
    DefaultChartProps & { data: PlotReportFactElement[]; customBenchmark?: CustomBenchmarkSuccessResponse | null }
> = ({ data, customBenchmark, ...delegated }) => {
  const { t } = useTranslation();
  const chartData = getChartData(data);
  const benchmarkValues = [
    customBenchmark?.low_value,
    customBenchmark?.moderate_value,
    customBenchmark?.high_value,
  ].filter((value): value is number => typeof value === 'number');

  if (!chartData || chartData.length === 0) {
    Logger.error('Not enough data to render chart');
    return (
      <div className='relative flex h-full w-full flex-col justify-center' {...delegated}>
        <NoChartData />
      </div>
    );
  }

  return (
    <Chart.Container
      {...delegated}
      data={chartData}
      config={{
        count: { label: t('shared.ncaDetail.details.invasiveSpecies.graph.yAxisTitle'), color: BAR_COLOR },
      }}
    >
      <BarChart data={chartData} margin={{ bottom: 80 }}>
        <defs>
          <linearGradient id='invasiveSpecies' x1='0' y1='-2.6' x2='0' y2='1'>
            <stop offset='0%' stopColor={BAR_COLOR} stopOpacity={0} />
            <stop offset='100%' stopColor={BAR_COLOR} stopOpacity={1} />
          </linearGradient>
        </defs>
        <Bar
          dataKey='count'
          fill='url(#invasiveSpecies)'
          radius={[RECTANGLE_RADIUS, RECTANGLE_RADIUS, 0, 0]}
          activeBar={<CustomActiveBar ry={RECTANGLE_RADIUS} fill={BAR_COLOR} />}
          barSize={40}
        />
        {typeof customBenchmark?.low_value === 'number' &&
          RenderBenchmarkReferenceLine({
            value: customBenchmark.low_value,
            benchmark: BenchmarkType.low,
            labelOverride: getBenchmarkLabel(true, t('global.benchmark.low'), customBenchmark.name),
          })}
        {typeof customBenchmark?.moderate_value === 'number' &&
          RenderBenchmarkReferenceLine({
            value: customBenchmark.moderate_value,
            benchmark: BenchmarkType.moderate,
            labelOverride: getBenchmarkLabel(true, t('global.benchmark.moderate'), customBenchmark.name),
          })}
        {typeof customBenchmark?.high_value === 'number' &&
          RenderBenchmarkReferenceLine({
            value: customBenchmark.high_value,
            benchmark: BenchmarkType.high,
            labelOverride: getBenchmarkLabel(true, t('global.benchmark.high'), customBenchmark.name),
          })}
        <Chart.XAxis
          dataKey='category'
          type='category'
          interval={0}
          className='invasive-species-x-axis'
          tick={(props: unknown) => <CustomTick {...(props as CustomTickProps)} />}
        />
        <Chart.YAxis
          allowDecimals={false}
          domain={[0, (dataMax: number) => Math.max(dataMax, ...benchmarkValues)]}
          className='invasive-species-y-axis'
        >
          <Chart.Label>{t('shared.ncaDetail.details.invasiveSpecies.graph.yAxisTitle')}</Chart.Label>
        </Chart.YAxis>
        <Chart.Tooltip content={<CustomTooltip />} cursor={false} />
      </BarChart>
    </Chart.Container>
  );
};

type CustomTickProps = SVGProps<SVGElement> & { payload: { value: string } };

const CustomTick = ({ x, y, payload }: CustomTickProps) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={10}
        y={-5}
        textAnchor='end'
        transform='rotate(-45)'
        style={{ fontSize: '12px', fill: 'rgba(0, 0, 0, 0.60)' }}
      >
        {payload.value}
      </text>
    </g>
  );
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
  if (!active || !payload?.length) return null;

  return (
    <ChartTooltip>
      <Stack direction='row' spacing={4}>
        <ChartTooltipTextSecondary>{label}</ChartTooltipTextSecondary>
        <ChartTooltipTextPrimary>{payload[0]?.value}</ChartTooltipTextPrimary>
      </Stack>
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipInvasiveSpecies };
